import React from 'react'
import { colorOptions, screenColorOptions, fabricColorOptions, bottomfabricColorOptions } from './colors'
import { builderBus } from './builderBus'

const FONT_HEADER = "'roc-grotesk-wide', sans-serif"
const FONT_BODY = 'Jost, sans-serif'

export default function ShareContent({ shareUrl, screenshotUrl, config, onGenerateShareLink }) {
  const c = config || {}
  const colorLabel = c.colorLabel || colorOptions[c.color] || c.color || '—'
  const screenTypeLabel = c.screenTypeLabel || c.screenType || '—'
  const screenColorLabel = c.screenColorLabel || (screenColorOptions && screenColorOptions[c.screenColor]) || c.screenColor || '—'
  const fabricColorLabel = c.fabricColorLabel || (fabricColorOptions && fabricColorOptions[c.fabricColor]) || c.fabricColor || '—'
  const bottomFabricColorLabel =
    !c.bottomFabricColor || c.bottomFabricColor === 'none'
      ? 'No Fill'
      : c.bottomFabricColorLabel || (bottomfabricColorOptions && bottomfabricColorOptions[c.bottomFabricColor]) || c.bottomFabricColor || 'No Fill'
  const bottomScreenTypeLabel = c.bottomScreenType === 'Logo' ? 'Ohma Logo' : c.bottomScreenType === 'Custom Logo' ? 'Custom Logo' : 'None'
  const microphoneTypeLabel = c.microphoneTypeLabel || c.microphoneType || '—'
  const popFilterColorLabel =
    c.popFilterColorLabel ||
    (c.popFilterMaterial === 'plastic'
      ? 'Black (Plastic)'
      : (['#232221', '#1c1d1d', '#1a1a1a', '#000000', '#000'].includes((c.popFilterColor || '').toLowerCase())
          ? 'Black'
          : colorOptions[c.popFilterColor] || c.popFilterColor || '—') + ' (Metal)')

  const [linkCopied, setLinkCopied] = React.useState(false)
  const [isGeneratingLink, setIsGeneratingLink] = React.useState(false)
  const [showAllDetails, setShowAllDetails] = React.useState(false)
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 || window.innerHeight >= window.innerWidth : false
  )
  const [isIpad, setIsIpad] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 && window.innerWidth <= 1024 && window.innerHeight > window.innerWidth : false
  )

  const fileId = React.useMemo(() => {
    if (!shareUrl) return null
    const match = shareUrl.match(/\/([0-9]+)\/?$/)
    return match ? match[1] : null
  }, [shareUrl])
  const buildFilename = fileId ? `my-ohma-mic-build-${fileId}.png` : 'my-ohma-microphone.png'

  React.useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth < 768 || window.innerHeight >= window.innerWidth)
      setIsIpad(window.innerWidth >= 768 && window.innerWidth <= 1024 && window.innerHeight > window.innerWidth)
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const copyToClipboard = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setLinkCopied(true)
        setTimeout(() => setLinkCopied(false), 2000)
      })
    }
  }

  const handleDownload = () => {
    if (screenshotUrl) {
      const a = document.createElement('a')
      a.href = screenshotUrl
      a.download = buildFilename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  const handleSaveToBuilds = () => {
    if (typeof builderBus.saveDesign === 'function') {
      builderBus.saveDesign()
      setBuildSaved(true)
      setTimeout(() => setBuildSaved(false), 2500)
    }
  }

  const handleGenerateLink = async () => {
    if (onGenerateShareLink) {
      setIsGeneratingLink(true)
      await onGenerateShareLink()
      setIsGeneratingLink(false)
    }
  }

  const buildShareText = () => {
    const allRows = [...primaryDetails, ...extraDetails]
    const lines = allRows.map((r) => r.label + ': ' + r.value).join('\n')
    return (shareUrl ? 'Check out my custom Ohma mic build:\n' + shareUrl + '\n\n' : '') + lines
  }

  // Get the screenshot as a File for native sharing. Tries the blob URL,
  // falls back to a data URL if screenshotUrl is a data: string.
  const getScreenshotFile = async () => {
    if (!screenshotUrl) return null
    try {
      const response = await fetch(screenshotUrl)
      const blob = await response.blob()
      return new File([blob], buildFilename, { type: blob.type || 'image/png' })
    } catch (e) {
      return null
    }
  }

  // Share with image if possible, fall back to text-only
  const shareWithFile = async (text) => {
    const file = await getScreenshotFile()
    if (file && navigator.share && navigator.canShare({ files: [file], text })) {
      await navigator.share({ text, files: [file] })
      return true
    }
    if (navigator.share) {
      await navigator.share({ text })
      return true
    }
    return false
  }

  const handleMessage = async () => {
    const text = buildShareText()
    try {
      const shared = await shareWithFile(text)
      if (!shared) window.open('sms:?body=' + encodeURIComponent(text))
    } catch (e) {
      if (e && e.name !== 'AbortError') window.open('sms:?body=' + encodeURIComponent(text))
    }
  }

  const handleInstagram = async () => {
    const fn = builderBus.generateInstagramScreenshot
    if (typeof fn !== 'function') {
      handleDownload()
      return
    }
    try {
      const blob = await fn()
      if (!blob) return
      const file = new File([blob], buildFilename, { type: 'image/png' })
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: 'My Ohma Mic Build', files: [file] })
      } else {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = buildFilename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 100)
      }
    } catch (e) {}
  }

  const handleEmail = async () => {
    const text = buildShareText()
    const file = await getScreenshotFile()
    if (file && navigator.share && navigator.canShare({ files: [file], text })) {
      try {
        await navigator.share({ text, files: [file] })
        return
      } catch (e) {
        if (e && e.name === 'AbortError') return
      }
    }
    const subject = encodeURIComponent('My Custom Ohma Mic Build')
    const body = encodeURIComponent(text)
    window.open('mailto:?subject=' + subject + '&body=' + body)
  }

  const handleMore = async () => {
    const text = buildShareText()
    try {
      const shared = await shareWithFile(text)
      if (!shared) copyToClipboard()
    } catch (e) {
      if (e && e.name !== 'AbortError') copyToClipboard()
    }
  }

  const primaryDetails = [
    { label: 'Microphone Type', value: microphoneTypeLabel },
    { label: 'Body Color', value: colorLabel },
    { label: 'Screen Type', value: screenTypeLabel },
    { label: 'Screen Color', value: screenColorLabel }
  ]
  const extraDetails = [
    { label: 'Fabric Color', value: fabricColorLabel },
    { label: 'Logo Fill', value: bottomFabricColorLabel },
    { label: 'Logo', value: bottomScreenTypeLabel },
    c.popFilterEnabled ? { label: 'Pop Filter', value: c.popFilterMaterial === 'plastic' ? 'Black (Plastic)' : popFilterColorLabel } : null,
    c.shockMountEnabled ? { label: 'Shock Mount', value: colorOptions[c.shockMountColor] || c.shockMountColor || 'Yes' } : null,
    c.padEnabled && c.microphoneType !== 'Ribbon' ? { label: 'Pad', value: 'Added' } : null
  ]
    .filter(Boolean)
    .filter((r) => r.value && r.value !== '—')

  const [copyHover, setCopyHover] = React.useState(false)
  const btnPrimary = {
    width: '100%',
    padding: '13px 20px',
    background: linkCopied ? '#E89B88' : copyHover ? '#5a7fe6' : '#6186f7',
    color: '#fff',
    border: 'none',
    borderRadius: '30px',
    cursor: 'pointer',
    fontFamily: 'Jost, sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    letterSpacing: 'normal',
    textTransform: 'none',
    transition: 'background 0.15s',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }

  const btnSecondary = {
    flex: 1,
    padding: '10px 16px',
    background: '#eeeeef',
    color: '#808082',
    border: 'none',
    borderRadius: '30px',
    cursor: 'pointer',
    fontFamily: 'Jost, sans-serif',
    fontWeight: 600,
    fontSize: '13px',
    letterSpacing: 'normal',
    textTransform: 'none',
    transition: 'background 0.15s',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }

  const DetailRow = ({ label, value, last }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '9px 14px',
        borderBottom: last ? 'none' : '1px solid #e8e8e6'
      }}>
      <span style={{ fontFamily: FONT_HEADER, fontWeight: 600, fontSize: 12, letterSpacing: '0.05em', color: '#1f2937' }}>{label}</span>
      <span style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#6b7280' }}>{value}</span>
    </div>
  )

  const ShareIcon = ({ label, onClick, children }) => (
    <div
      onClick={onClick}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}
      onMouseEnter={(e) => e.currentTarget.querySelector('img') && (e.currentTarget.querySelector('img').style.transform = 'scale(1.1)')}
      onMouseLeave={(e) => e.currentTarget.querySelector('img') && (e.currentTarget.querySelector('img').style.transform = 'scale(1)')}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.15s' }}>{children}</div>
      <span style={{ fontFamily: FONT_BODY, fontSize: 11, color: '#6b7280' }}>{label}</span>
    </div>
  )

  const [detailsCopied, setDetailsCopied] = React.useState(false)
  const [buildSaved, setBuildSaved] = React.useState(false)
  const copyBuildDetails = () => {
    const text = buildShareText()
    navigator.clipboard.writeText(text).then(() => {
      setDetailsCopied(true)
      setTimeout(() => setDetailsCopied(false), 2000)
    })
  }

  const BuildDetails = () => (
    <div style={{ background: '#f5f5f3', borderRadius: 10, overflow: 'hidden' }}>
      {/* Header row with copy icon inline */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #e8e8e6', flexShrink: 0 }}>
        <span style={{ flex: 1, fontFamily: FONT_HEADER, fontWeight: 700, fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#333' }}>
          Build Details
        </span>
        <button
          onClick={copyBuildDetails}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            color: detailsCopied ? '#5a9a6e' : '#9ca3af',
            fontSize: 11,
            fontFamily: FONT_BODY,
            transition: 'color 0.15s',
            width: 60,
            flexShrink: 0,
            justifyContent: 'flex-end'
          }}>
          {detailsCopied ? (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Copied!</span>
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      {/* Scrollable rows */}
      <div>
        {primaryDetails.map((r, i) => (
          <DetailRow key={i} label={r.label} value={r.value} last={false} />
        ))}
        {showAllDetails && extraDetails.map((r, i) => <DetailRow key={i + 4} label={r.label} value={r.value} last={i === extraDetails.length - 1} />)}
        <button
          onClick={() => setShowAllDetails((v) => !v)}
          style={{
            width: '100%',
            padding: '10px',
            background: 'none',
            border: 'none',
            borderTop: '1px solid #e8e8e6',
            cursor: 'pointer',
            fontFamily: FONT_BODY,
            fontSize: 13,
            color: '#6b7280',
            textAlign: 'center',
            transition: 'color 0.15s'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#333')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#6b7280')}>
          {showAllDetails ? '− Less' : '+ More Details'}
        </button>
      </div>
    </div>
  )

  // ── Mobile ─────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ padding: '4px 0 24px' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 26, fontWeight: 700, fontFamily: FONT_HEADER, textAlign: 'center' }}>Your Ohma Mic Build</h2>

        <div
          style={{
            background: '#91C3DE',
            borderRadius: 12,
            aspectRatio: '1/1',
            width: 'calc(100% - 32px)',
            margin: '0 16px 20px',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          {screenshotUrl ? (
            <img src={screenshotUrl} alt="Your build" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <style>{`@keyframes ohma-spin{to{transform:rotate(360deg)}} .ohma-spinner{animation:ohma-spin 0.9s linear infinite}`}</style>
              <svg
                className="ohma-spinner"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="rgba(255,255,255,0.9)"
                strokeWidth="2.5"
                strokeLinecap="round">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, fontFamily: FONT_BODY }}>Generating preview...</span>
            </div>
          )}
        </div>

        {!shareUrl ? (
          <button onClick={handleGenerateLink} disabled={isGeneratingLink} style={{ ...btnPrimary, marginBottom: 10 }}>
            {isGeneratingLink ? 'Generating...' : 'Generate Share Link'}
          </button>
        ) : (
          <button
            onClick={copyToClipboard}
            style={{ ...btnPrimary, marginBottom: 10 }}
            onMouseEnter={() => setCopyHover(true)}
            onMouseLeave={() => setCopyHover(false)}>
            {linkCopied ? '✓ Copied!' : 'Copy Link'}
          </button>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
          <button
            onClick={handleSaveToBuilds}
            style={{ ...btnSecondary, background: buildSaved ? '#E89B88' : '#eeeeef', color: buildSaved ? '#fff' : '#808082' }}>
            {buildSaved ? '✓ Saved!' : 'Save to My Builds'}
          </button>
          <button onClick={handleDownload} style={btnSecondary}>
            Download
          </button>
        </div>

        <p style={{ margin: '16px 0 16px', fontFamily: FONT_BODY, fontWeight: 700, fontSize: '0.83em', letterSpacing: '0.04em', color: '#333' }}>Share It</p>
        <div style={{ display: 'flex', marginBottom: 22 }}>
          <ShareIcon label="Message" onClick={handleMessage}>
            <img src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/Messages.svg" width="52" height="52" alt="Message" style={{ borderRadius: '50%' }} />
          </ShareIcon>
          <ShareIcon label="Instagram" onClick={handleInstagram}>
            <img src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/IG.svg" width="52" height="52" alt="Instagram" style={{ borderRadius: '50%' }} />
          </ShareIcon>
          <ShareIcon label="Email" onClick={handleEmail}>
            <img src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/Email.svg" width="52" height="52" alt="Email" style={{ borderRadius: '50%' }} />
          </ShareIcon>
          <ShareIcon label="More" onClick={handleMore}>
            <img src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/More-Options.svg" width="52" height="52" alt="More" style={{ borderRadius: '50%' }} />
          </ShareIcon>
        </div>

        <BuildDetails />
      </div>
    )
  }

  // ── Desktop ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100%', gap: 0 }}>
      {/* Left — screenshot */}
      <div
        style={{
          flex: '0 0 45%',
          background: '#91C3DE',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
        {screenshotUrl ? (
          <img src={screenshotUrl} alt="Your build" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <style>{`@keyframes ohma-spin{to{transform:rotate(360deg)}} .ohma-spinner{animation:ohma-spin 0.9s linear infinite}`}</style>
            <svg
              className="ohma-spinner"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255,255,255,0.9)"
              strokeWidth="2.5"
              strokeLinecap="round">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontFamily: FONT_BODY, letterSpacing: '0.05em' }}>Generating preview...</span>
          </div>
        )}
        <button
          onClick={handleDownload}
          title="Download"
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '50%',
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background 0.15s',
            color: '#555'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
          </svg>
        </button>
      </div>

      {/* Right — content */}
      <div style={{ flex: '0 0 55%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '32px 40px 24px' }}>
          <h2 style={{ margin: '0 0 18px', fontFamily: FONT_HEADER, fontWeight: 700, fontSize: 26, textAlign: 'left', letterSpacing: '0.02em' }}>
            Your Ohma Mic Build
          </h2>

          {!shareUrl ? (
            <button onClick={handleGenerateLink} disabled={isGeneratingLink} style={{ ...btnPrimary, marginBottom: 10 }}>
              {isGeneratingLink ? 'Generating...' : 'Generate Share Link'}
            </button>
          ) : (
            <button
              onClick={copyToClipboard}
              style={{ ...btnPrimary, marginBottom: 10 }}
              onMouseEnter={() => setCopyHover(true)}
              onMouseLeave={() => setCopyHover(false)}>
              {linkCopied ? '✓ Link Copied!' : 'Copy Link'}
            </button>
          )}

          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <button
              onClick={handleSaveToBuilds}
              style={{ ...btnSecondary, background: buildSaved ? '#E89B88' : '#eeeeef', color: buildSaved ? '#fff' : '#808082' }}
              onMouseEnter={(e) => {
                if (!buildSaved) e.currentTarget.style.background = '#dcdcdb'
              }}
              onMouseLeave={(e) => {
                if (!buildSaved) e.currentTarget.style.background = '#eeeeef'
              }}>
              {buildSaved ? '✓ Saved!' : 'Save to My Builds'}
            </button>
            <button
              onClick={handleDownload}
              style={btnSecondary}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#dcdcdb')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#eeeeef')}>
              Download
            </button>
          </div>

          <p style={{ margin: '16px 0 16px', fontFamily: FONT_BODY, fontWeight: 700, fontSize: '0.83em', letterSpacing: '0.04em', color: '#333' }}>Share It</p>
          <div style={{ display: 'flex', marginBottom: 20 }}>
            <ShareIcon label="Message" onClick={handleMessage}>
              <img
                src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/Messages.svg"
                width="44"
                height="44"
                alt="Message"
                style={{ borderRadius: '50%' }}
              />
            </ShareIcon>
            <ShareIcon label="Instagram" onClick={handleInstagram}>
              <img src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/IG.svg" width="44" height="44" alt="Instagram" style={{ borderRadius: '50%' }} />
            </ShareIcon>
            <ShareIcon label="Email" onClick={handleEmail}>
              <img src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/Email.svg" width="44" height="44" alt="Email" style={{ borderRadius: '50%' }} />
            </ShareIcon>
            <ShareIcon label="More" onClick={handleMore}>
              <img
                src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/More-Options.svg"
                width="44"
                height="44"
                alt="More"
                style={{ borderRadius: '50%' }}
              />
            </ShareIcon>
          </div>

          <BuildDetails />
        </div>
        {/* end scroll */}
      </div>
      {/* end right col */}
    </div>
  )
}
